<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import {
  isGuardedMediaUrl,
  useGuardedMedia,
} from 'dashboard/composables/useGuardedMedia';
import BaseBubble from './Base.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { CONTENT_TYPES, MESSAGE_VARIANTS, ORIENTATION } from '../constants';
import FormattedContent from './Text/FormattedContent.vue';
import { detectMediaTones as sampleMediaTones } from './mediaTone';
import StickerLoopCanvas from './StickerLoopCanvas.vue';
import {
  classifyPhotoAspectRatio,
  classifyGifAspectRatio,
  gifFrameStyle,
  photoFrameStyle,
  captionReservedWidthStyle,
} from './mediaAspectLayout';

import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const {
  filteredCurrentChatAttachments,
  currentChatAttachments,
  attachments,
  content,
  contentType,
  contentAttributes,
  orientation,
  variant,
} = useMessageContext();

const attachment = computed(() => {
  return attachments.value[0];
});

// Aceleres 2026-09-09: o blob do proprio ficheiro que o atendente acabou de
// enviar, carregado ate aqui por `mergeLocalMediaPreviews` (helper/commons.js).
// Nao e uma miniatura: sao os bytes exactos da imagem final, ja no navegador.
// Por isso desenha-se NITIDO, sem o desfoque da miniatura — quando o remoto
// acabar de carregar, a troca e invisivel.
const localPreviewUrl = computed(() => {
  const declarado =
    attachment.value?.localPreviewUrl ||
    attachment.value?.local_preview_url ||
    '';
  if (declarado) return declarado;

  // Na mensagem OTIMISTA (a que existe entre o clique em enviar e a resposta do
  // servidor) o proprio `dataUrl` ja e o object URL do ficheiro escolhido. Sem
  // este ramo, a bolha otimista de uma foto grande ficava cinzenta enquanto o
  // `loadWithRetry` descodificava o blob fora do DOM — e so depois desenhava um
  // `<img>` que tinha de descodificar tudo outra vez. Medido em homologacao:
  // 1,3 s de balao vazio numa foto de 4,9 MB, com os bytes ja na memoria.
  const dataUrl = attachment.value?.dataUrl || attachment.value?.data_url || '';
  return String(dataUrl).startsWith('blob:') ? dataUrl : '';
});

const previewUrl = computed(
  () =>
    localPreviewUrl.value ||
    attachment.value?.thumbUrl ||
    attachment.value?.thumb_url ||
    ''
);
const isLocalPreview = computed(
  () =>
    Boolean(localPreviewUrl.value) && previewUrl.value === localPreviewUrl.value
);
const previewFailed = ref(false);

const { guardedMediaSrc, guardedMediaFailed, retryGuardedMedia } =
  useGuardedMedia();

const rawMediaUrl = computed(
  () => attachment.value?.dataUrl || attachment.value?.data_url || ''
);

// O endpoint guardado (`/attachment_downloads/...`) exige auth. Num
// `<img src>` ou `new Image()` o navegador pede sem cabeçalho e leva 401 —
// a bolha caía em "Não foi possível carregar esta mídia".
const displaySrc = computed(() => {
  const url = rawMediaUrl.value;
  if (!url) return '';
  if (isGuardedMediaUrl(url)) return guardedMediaSrc(url);
  return url;
});

const { isLoaded, hasError, loadWithRetry, resetLoadState } = useLoadWithRetry({
  max_retry: 5,
  backoff: 800,
});

const showUnavailable = computed(
  () =>
    hasError.value &&
    !localPreviewUrl.value &&
    (!previewUrl.value || previewFailed.value)
);

const showGallery = ref(false);
const senderTone = ref('light');
const chevronTone = ref('light');
const timeTone = ref('light');
const isCaptionExpanded = ref(false);
const CAPTION_TRUNCATE_LENGTH = 768;
const caption = computed(() => {
  let value = String(content.value || '').trim();
  const normalizedValue = value.toLowerCase();
  const technicalMediaLabels = [
    'mensagem recebida',
    '[mensagem recebida]',
    'message received',
    '[message received]',
    'mídia recebida',
    '[mídia recebida]',
    'media received',
    '[media received]',
    '[sticker recebido]',
    '[sticker received]',
  ];

  if (technicalMediaLabels.includes(normalizedValue)) return '';

  // Aceleres: quando o rotulo tecnico vem colado como PREFIXO do texto real
  // (ex.: "[Mensagem recebida]\n\ntexto"), o match exato acima nao pega e
  // sobra o rotulo + linhas em branco antes da legenda, parecendo um vao
  // vazio na bolha. Corta so o prefixo tecnico nesse caso.
  const matchedLabel = technicalMediaLabels.find(label =>
    normalizedValue.startsWith(label)
  );
  if (matchedLabel) {
    value = value.slice(matchedLabel.length).trim();
  }

  return value;
});
const hasCaption = computed(() => Boolean(caption.value));
// Pedido 2026-09-14: a legenda longa era MEDIDA em unidades UTF-16 e CORTADA
// em caracteres. Cada emoji vale duas unidades, entao 512 emojis (1024
// unidades) passavam como longos e o corte de 768 caracteres nao tirava nada:
// a bolha mostrava tudo e ainda um "Ler mais" sem efeito. Agora o corte usa a
// mesma medida, parando no ultimo caractere inteiro para nao partir um emoji.
const isLongCaption = computed(
  () => caption.value.length > CAPTION_TRUNCATE_LENGTH
);
const truncatedCaption = computed(() => {
  if (!isLongCaption.value) return caption.value;
  let truncated = '';
  [...caption.value].every(character => {
    if (truncated.length + character.length > CAPTION_TRUNCATE_LENGTH) {
      return false;
    }
    truncated += character;
    return true;
  });
  return truncated;
});
const visibleCaption = computed(() =>
  isLongCaption.value && !isCaptionExpanded.value
    ? truncatedCaption.value
    : caption.value
);
const readMoreLabel = 'Ler mais';

const wameMediaType = computed(() =>
  String(
    contentAttributes.value?.wameMediaType ||
      contentAttributes.value?.wame_media_type ||
      ''
  ).toLowerCase()
);

const attachmentExtension = computed(() =>
  String(attachment.value?.extension || '').toLowerCase()
);

const attachmentContentType = computed(() =>
  String(
    attachment.value?.contentType || attachment.value?.content_type || ''
  ).toLowerCase()
);

const isSticker = computed(
  () =>
    contentType.value === CONTENT_TYPES.STICKER ||
    wameMediaType.value === 'sticker'
);

const isGif = computed(
  () =>
    !isSticker.value &&
    (wameMediaType.value === 'gif' ||
      attachmentExtension.value === 'gif' ||
      attachmentContentType.value === 'image/gif')
);

const isPrivatePhoto = computed(
  () =>
    variant.value === MESSAGE_VARIANTS.PRIVATE &&
    !isSticker.value &&
    !isGif.value
);

const loadedImageWidth = ref(0);
const loadedImageHeight = ref(0);

const naturalImageDimensions = computed(() => ({
  width: loadedImageWidth.value || Number(attachment.value?.width || 0),
  height: loadedImageHeight.value || Number(attachment.value?.height || 0),
}));

const imageAspectRatio = computed(() => {
  const { width, height } = naturalImageDimensions.value;

  return width > 0 && height > 0 ? width / height : null;
});

// Aceleres 2026-08-12: classificacao da FOTO pelo aspect ratio real, com os
// limiares da regua da foto (0.6 / 0.85 / 1.15 / 1.8). Serve tanto para o
// tamanho (via photoFrameStyle, abaixo) quanto para o `data-aspect-class` que
// fica no DOM — util para inspecionar no navegador em que balde a foto caiu.
const imageAspectClass = computed(() => {
  if (isSticker.value || isGif.value) return null;
  return classifyPhotoAspectRatio(imageAspectRatio.value);
});

// Aceleres 2026-08-12: GIF de verdade (.gif) segue a mesma regua do GIF que
// chega como MP4 mudo pelo WhatsApp (Video.vue) — a caixa-alvo por formato,
// nao o tamanho natural do arquivo. Fica fora de `imageAspectClass` de
// proposito: aquela classificacao e a da FOTO, com limiares e caixas
// diferentes.
const gifAspectClass = computed(() =>
  isGif.value ? classifyGifAspectRatio(imageAspectRatio.value) : null
);

// Aceleres 2026-08-12: UNICA fonte do tamanho da foto. Devolve so custom
// properties CSS (--acl-photo-*), lidas pelo "BLOCO UNICO DE TAMANHO DA FOTO"
// em aceleres-overrides.css. Nao depende da direcao da mensagem. A legenda so
// entra para a foto estreita encher a caixa (ver photoFrameStyle).
const imageAssetFrameStyle = computed(() => {
  if (isGif.value) {
    // Sem dimensoes ainda: so o teto de largura, sem altura nenhuma.
    return gifFrameStyle(imageAspectRatio.value) || { maxWidth: '100%' };
  }
  const { width, height } = naturalImageDimensions.value;
  return photoFrameStyle(width, height, {
    captioned: hasCaption.value || isPrivatePhoto.value,
  });
});

const imageAssetClass = computed(() => {
  // No GIF o frame ja carrega a proporcao exata do arquivo: encher o frame
  // com `contain` nao corta nem deforma, e mantem o GIF no tamanho da
  // caixa-alvo em vez do tamanho natural (que deixava GIF pequeno minusculo).
  if (isGif.value) return 'w-full h-full object-contain !bg-white';
  if (isSticker.value) return '!bg-white';
  // Foto: nenhuma classe de tamanho aqui, de proposito. Largura, altura e
  // object-fit vem das variaveis escritas por `imageAssetFrameStyle` — ter
  // tambem uma classe Tailwind com medida seria uma segunda fonte para a
  // mesma decisao, e era assim que o tamanho "voltava atras" a cada ajuste.
  // O fundo só aparece através dos pixels transparentes: fotos enviadas usam
  // o verde da superfície de saída e recebidas continuam brancas.
  return orientation.value === ORIENTATION.RIGHT
    ? '!bg-[#d8fdd2]'
    : '!bg-white';
});

// Aceleres 2026-08-17: enquanto a foto/GIF nao reportou o tamanho, a bolha com
// legenda reserva a largura em vez de encolher em cima do texto. Ver
// captionReservedWidthStyle. `imageAspectRatio` e o mesmo sinal que o resto do
// componente usa para saber se ja ha medida — nao ha segunda checagem aqui.
// Aceleres 2026-08-17: liga quando o StickerLoopCanvas conseguiu assumir o
// desenho da figurinha animada. So entao o <img> some — ate la ele e o que
// aparece, e continua sendo sempre quem da o tamanho da bolha.
const stickerCanvasActive = ref(false);

// Aceleres 2026-08-17 (segunda passagem): a condicao era `!imageAspectRatio`,
// e por isso a reserva quase nunca entrava. Os dois sinais nao sao o mesmo:
//
//   - `imageAspectRatio` ja e verdadeiro no PRIMEIRO render, porque
//     `naturalImageDimensions` cai em `attachment.width/height` e o servidor
//     manda essas medidas (o Active Storage analisa o blob: conferido no banco,
//     `{"width":400,"height":400,"analyzed":true}`);
//   - o FRAME so entra no DOM com `v-else-if="isLoaded"`, ou seja, quando os
//     bytes da imagem chegaram.
//
// Entre um e outro a bolha ficava sem nenhum elemento com largura, e como ela e
// `!w-fit` quem passava a mandar era a legenda: o texto espremia ate ao minimo e
// abria de novo quando a foto aparecia. Era esse aperto que se via.
//
// A reserva permanece depois do carregamento. O frame define a mesma largura,
// mas pode ser comprimido pelo layout flex no mobile; manter o minimo impede
// que uma legenda sem espacos reduza a bolha a uma coluna de um caractere.
// O valor sai de `imageAssetFrameStyle`, a mesma fonte que o frame usa — nao ha
// medida repetida aqui.
//
// Sai como CUSTOM PROPERTY, e nao como `min-width` inline, porque
// `aceleres-overrides.css` tem `min-width: 0 !important` na bolha da foto e do
// GIF — e `!important` de folha de estilo vence estilo inline. Era essa a razao
// de a reserva escrita antes nunca mudar nada aqui, enquanto no video (bolha sem
// esse `!important`) funcionava. O CSS le a variavel; o valor continua a vir
// daqui, como ja acontece com o resto do tamanho da foto.
const bubbleReservedStyle = computed(() => {
  if (!hasCaption.value || hasError.value) return null;

  const frame = imageAssetFrameStyle.value || {};
  const larguraFinal =
    frame['--acl-photo-frame-width'] ||
    frame.width ||
    captionReservedWidthStyle().minWidth;

  return { '--acl-bubble-min-width': larguraFinal };
});

// Mantem o espaco da midia enquanto a imagem ou o GIF ainda carrega. A caixa
// usa a mesma geometria do frame final; quando o anexo nao traz dimensoes,
// reaproveita a reserva da legenda e uma proporcao apenas provisoria.
const loadingFrameStyle = computed(() => {
  const frame = imageAssetFrameStyle.value || {};
  const hasFrameWidth =
    frame['--acl-photo-frame-width'] || frame.width || frame.minWidth;

  return {
    ...(!hasFrameWidth ? captionReservedWidthStyle() : {}),
    ...frame,
    aspectRatio:
      frame.aspectRatio ||
      imageAspectRatio.value ||
      (isSticker.value ? '1 / 1' : '4 / 3'),
  };
});

const imageDimensionsOrOriginal = computed(() => ({
  width: naturalImageDimensions.value.width || undefined,
  height: naturalImageDimensions.value.height || undefined,
}));

let loadGeneration = 0;
const mediaLoadErrorLabel = 'Não foi possível carregar esta mídia.';
const retryMediaLabel = 'Tentar novamente';

const loadImage = async url => {
  loadGeneration += 1;
  const generation = loadGeneration;
  resetLoadState();
  if (!url) return;

  const dimensions = await loadWithRetry(url);
  if (generation !== loadGeneration || !dimensions) return;

  loadedImageWidth.value = Number(dimensions.width || 0);
  loadedImageHeight.value = Number(dimensions.height || 0);
};

const retryMediaLoad = async () => {
  const url = rawMediaUrl.value;
  if (isGuardedMediaUrl(url)) {
    const resolved = await retryGuardedMedia(url);
    if (resolved) {
      await loadImage(resolved);
      return;
    }
    hasError.value = true;
    isLoaded.value = false;
    return;
  }
  loadImage(url);
};
const handlePreviewError = () => {
  previewFailed.value = true;
};

const retryFailedMediaWhenVisible = () => {
  if (document.hidden || !hasError.value) return;
  retryMediaLoad();
};

onMounted(() => {
  retryMediaLoad();
  window.addEventListener('online', retryFailedMediaWhenVisible);
  document.addEventListener('visibilitychange', retryFailedMediaWhenVisible);
});

onBeforeUnmount(() => {
  loadGeneration += 1;
  window.removeEventListener('online', retryFailedMediaWhenVisible);
  document.removeEventListener('visibilitychange', retryFailedMediaWhenVisible);
});

watch(displaySrc, (nextUrl, previousUrl) => {
  if (nextUrl && nextUrl !== previousUrl) loadImage(nextUrl);
});

watch(
  () => guardedMediaFailed(rawMediaUrl.value),
  failed => {
    if (failed && !displaySrc.value && !localPreviewUrl.value) {
      hasError.value = true;
      isLoaded.value = false;
    }
  }
);

watch(previewUrl, () => {
  previewFailed.value = false;
});

// Aceleres 2026-08-12: mesma regra do MP4 tratado como GIF (Video.vue) — o GIF
// abre sozinho, fora da galeria da conversa, e nao entra na navegacao entre as
// fotos.
//
// A figurinha abre isolada no computador e no telemovel. GalleryView recebe
// mobileAttachments para as outras midias, mas nao as usa para a figurinha.
//
// Foto e GIF navegam pelo histórico da própria conversa. O provider resolve a
// lista pelo conversationId da mensagem, sem depender da seleção global.
const galleryAttachments = computed(() =>
  isSticker.value
    ? [useSnakeCase(attachment.value)]
    : filteredCurrentChatAttachments.value
);

const handleImageError = () => {
  if (!isLoaded.value) hasError.value = true;
};

const detectImageTones = event => {
  try {
    const image = event.currentTarget;
    const tones = sampleMediaTones(image);
    senderTone.value = tones.sender;
    chevronTone.value = tones.chevron;
    timeTone.value = tones.time;
  } catch {
    senderTone.value = 'light';
    chevronTone.value = 'light';
    timeTone.value = 'light';
  }
};

const handleImageLoad = event => {
  isLoaded.value = true;
  hasError.value = false;
  loadedImageWidth.value = Number(event.currentTarget?.naturalWidth || 0);
  loadedImageHeight.value = Number(event.currentTarget?.naturalHeight || 0);
  detectImageTones(event);
};
</script>

<template>
  <BaseBubble
    :class="[
      isSticker
        ? 'acl-sticker-bubble max-w-[min(320px,70vw)] p-0'
        : isGif
          ? 'acl-gif-bubble p-1'
          : 'acl-image-bubble !w-fit !max-w-[calc(100vw-48px)]',
      !isSticker && !isGif ? 'p-0' : '',
      showUnavailable ? 'w-[260px] max-w-[calc(100vw-5rem)]' : '',
    ]"
    :style="bubbleReservedStyle"
    show-sender-name
    media-caption
    :status-on-media="!hasCaption && !isSticker && !isGif && !showUnavailable"
    :data-bubble-name="isSticker ? 'sticker' : isGif ? 'gif' : 'image'"
    :data-media-state="showUnavailable ? 'unavailable' : undefined"
    :data-aspect-class="imageAspectClass || gifAspectClass || undefined"
    :data-sender-tone="senderTone"
    :data-chevron-tone="chevronTone"
    :data-time-tone="timeTone"
    role="button"
    tabindex="0"
    @click="showGallery = true"
    @keydown.enter.space.self.prevent="showGallery = true"
  >
    <div
      v-if="showUnavailable"
      class="acl-image-unavailable flex min-h-16 items-center gap-2 rounded-lg px-3 py-4 text-left"
    >
      <Icon
        icon="i-lucide-circle-off"
        class="size-4 flex-shrink-0 text-n-slate-11"
      />
      <div class="min-w-0">
        <p class="mb-1 text-sm leading-5 text-n-slate-11">
          {{ mediaLoadErrorLabel }}
        </p>
        <button
          type="button"
          class="text-sm font-medium text-n-brand underline"
          @click.stop="retryMediaLoad"
        >
          {{ retryMediaLabel }}
        </button>
      </div>
    </div>
    <div
      v-else-if="!isLoaded"
      aria-hidden="true"
      class="acl-image-bubble__asset-frame relative overflow-hidden"
      :class="{
        'rounded-lg': !isSticker,
        'acl-gif-media bg-black': isGif,
        'bg-n-alpha-2': !isGif,
      }"
      :style="loadingFrameStyle"
    >
      <img
        v-if="previewUrl && !previewFailed"
        class="pointer-events-none block"
        :class="[
          imageAssetClass,
          isLocalPreview
            ? isSticker
              ? 'max-h-[280px] max-w-full w-auto object-contain'
              : ''
            : 'scale-[1.03] blur-md opacity-80',
        ]"
        :src="previewUrl"
        :width="imageDimensionsOrOriginal.width"
        :height="imageDimensionsOrOriginal.height"
        alt=""
        decoding="async"
        @error="handlePreviewError"
      />
    </div>
    <!-- Aceleres 2026-08-12: `acl-gif-media` marca a area da MIDIA do GIF.
         Separa quem manda no tamanho (esta div) de quem so vem abaixo (a
         legenda), e tira o GIF dos tetos da FOTO: o frame e o mesmo elemento
         nas duas, entao sem essa marca o GIF herdava o max-height de 344px da
         foto e um GIF vertical saia achatado, com outra proporcao. -->
    <div
      v-else-if="isLoaded"
      class="acl-image-bubble__asset-frame relative group overflow-hidden"
      :class="{
        'rounded-lg': !isSticker,
        'acl-gif-media': isGif,
      }"
      :style="imageAssetFrameStyle"
    >
      <img
        class="block"
        :class="[
          isSticker
            ? 'max-h-[280px] max-w-full w-auto object-contain'
            : imageAssetClass,
          stickerCanvasActive ? 'invisible' : '',
        ]"
        :src="displaySrc"
        @error="handleImageError"
        :width="imageDimensionsOrOriginal.width"
        :height="imageDimensionsOrOriginal.height"
        :alt="$t('CHAT_LIST.ATTACHMENTS.image.CONTENT')"
        @load="handleImageLoad"
      />
      <!-- Aceleres 2026-08-17: a figurinha WebP animada traz `loop_count=0`
           (infinito) gravado no ficheiro, e isso o `<img>` obedece. O canvas
           redesenha a animacao contando 3 voltas; se nao der, nao aparece e
           fica tudo como estava. Ver StickerLoopCanvas.vue. -->
      <StickerLoopCanvas
        v-if="isSticker"
        :url="displaySrc"
        replay-on-hover
        @active="stickerCanvasActive = true"
        @inactive="stickerCanvasActive = false"
      />
      <div
        v-if="!isSticker && !isGif"
        class="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-24 bg-gradient-to-t from-black/45 via-black/10 to-transparent opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      />
      <div
        v-if="!isSticker && !isGif && orientation === ORIENTATION.RIGHT"
        class="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-black/30 via-black/10 to-transparent opacity-0 transition-opacity duration-150 group-hover:opacity-100"
      />
    </div>
    <div
      v-if="hasCaption"
      class="acl-image-bubble__caption acl-media-caption-surface box-border w-full min-h-9 px-2 pb-6 pt-2 text-[15.8px] leading-5"
      @click.stop
    >
      <span class="inline">
        <FormattedContent
          class="acl-message-caption [&_p]:!inline [&_p]:!m-0"
          :content="visibleCaption"
        />
        <button
          v-if="isLongCaption && !isCaptionExpanded"
          type="button"
          class="inline font-bold text-[#1b8755] no-underline -ml-px !m-0 !p-0 border-0 leading-none"
          @click.stop="isCaptionExpanded = true"
        >
          ... {{ readMoreLabel }}
        </button>
      </span>
    </div>
    <slot name="menu-trigger" :chevron-tone="chevronTone" />
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="galleryAttachments"
    :mobile-attachments="useSnakeCase(currentChatAttachments)"
    :is-sticker="isSticker"
    :initial-caption="caption"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
